import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, type Ref } from "vue";
import { request } from "@/shared/http/client";

/**
 * AS PERGUNTAS QUE NINGUÉM RESPONDEU — o que substitui a fila de propostas.
 *
 * The queue asked a person to judge twelve sentences a model pulled out of a
 * transcript, ~28.800 of them a month at V2's real traffic. This asks them to
 * answer one question real customers asked twenty-three times.
 *
 * And it dries up: a gap exists only because an agent got stuck, and agents get
 * stuck less as the brief fills in. The source shrinks as the product improves,
 * which is the opposite of stacking.
 */

export interface Gap {
	readonly id: string;
	readonly question: string;
	readonly context: string | null;
	/** Counting the ones asked in different words — grouped by meaning server-side. */
	readonly timesAsked: number;
	readonly answer: string | null;
	readonly answeredAt: string | null;
	/** Whether somebody already approved this answer into what the agents know (curated line or library). */
	readonly promoted: boolean;
	readonly askedAt: string;
	readonly status: "open" | "answered";
	/**
	 * O rascunho de conhecimento que a IA propôs a partir da resposta, e onde ela
	 * sugere que ele vá. Nulos até a resposta ter sido rascunhada. É uma PROPOSTA que
	 * uma pessoa aprova — nada entra na curadoria ou na library sem esse clique.
	 */
	readonly knowledgeDraft: string | null;
	readonly knowledgeKind: KnowledgeDestination | null;
	/** Por que a IA RECUSOU rascunhar — a frase dela, mostrada como está. Nulo enquanto nada foi decidido, e nulo sempre que há rascunho. */
	readonly knowledgeDeclined: string | null;
	/** Só para este agente (código): aprovada, vai para a memória ou a biblioteca dele. */
	readonly forAgent?: string | null;
	readonly forAgentName?: string | null;
}

/**
 * Para onde vai o que a pessoa aprovou.
 *
 * `curated` (o padrão) é uma linha de "o que a equipe ensinou" — tabela própria,
 * lida por todo agente em todo turno numa secção própria do prompt. `library` é
 * texto longo, buscado sob demanda. "Ficha da empresa" deixou de ser destino em
 * 08/09/2026: a ficha é só o que o dono escreve.
 */
export type KnowledgeDestination = "curated" | "library";

/**
 * As lacunas, e o relógio que faltava.
 *
 * Esta lista não tinha `refetchInterval` nenhum, e com `refetchOnWindowFocus`
 * desligado no cliente global isso é uma tela que só muda quando alguém troca de
 * rota ou dá F5. Não é detalhe: as lacunas do primeiro dia NASCEM sozinhas — o
 * apply da proposta abre-as, e a pipeline de curadoria escreve o rascunho de
 * conhecimento delas minutos depois. Quem estava a olhar não via nenhuma das
 * duas coisas acontecer.
 */
/**
 * QUAL LISTA — e `pending_curation` é a que esta tela é.
 *
 * `open` é "ninguém respondeu", e essa lista já é uma tela inteira: Pedidos de
 * ajuda, lida da MESMA `human_request`. Contá-la também aqui punha dois crachás
 * com o mesmo número no menu, e nenhum dos dois dizia se eram dez coisas ou
 * vinte.
 *
 * `pending_curation` é o trabalho que só existe deste lado: respondida, e ainda
 * FORA do que os agentes sabem. Responder já entregou a resposta a quem
 * esperava; pô-la na ficha ou na library é o gesto seguinte, e é de uma pessoa.
 */
export type GapStatus = "open" | "answered" | "pending_curation" | "all";

/** Por data. `recent` (as últimas primeiro) é o padrão do dono; `oldest` é o alternador. */
export type GapOrder = "recent" | "oldest";

/**
 * UMA PÁGINA da lista — o servidor pagina, a tela só diz qual.
 *
 * A curadoria pode ficar extremamente longa (pedido do dono), e uma lista que
 * pedia "as primeiras 50" não tinha página 2. `limit`/`offset` é o mesmo par
 * de `/v3/memory` e `/v3/contacts`, e o `total` da resposta é o denominador da
 * `Pagination`. Quem só quer contar (o crachá do menu, a Home) não passa nada
 * e recebe a primeira página como sempre recebeu.
 */
export interface GapPage {
	readonly limit: number;
	readonly offset: number;
	readonly order?: GapOrder;
}

// SEM relógio (27/08, zero polling): esta lista atualiza por foco e pelas
// mutações da própria curadoria. O rascunho que nasce sozinho minutos depois
// aparece quando a pessoa volta ao ecrã — o preço de não ter um SSE de memória
// ainda; quando ele existir, é ele que invalida esta chave.
export function useGaps(
	status: Ref<GapStatus>,
	interval: number | false | null = null,
	page?: Ref<GapPage>,
) {
	return useQuery({
		queryKey: computed(() => ["memory", "gaps", status.value, page?.value ?? null]),
		queryFn: () => {
			const params = new URLSearchParams({ status: status.value });
			if (page?.value) {
				params.set("limit", String(page.value.limit));
				params.set("offset", String(page.value.offset));
				if (page.value.order) params.set("order", page.value.order);
			}
			return request<{ rows: Gap[]; total: number }>(`/v3/memory/gaps?${params}`);
		},
		refetchInterval: interval !== null ? interval : false,
		refetchOnWindowFocus: true,
		// A página que muda não apaga a anterior enquanto a próxima chega: o total
		// e as linhas ficam, e a tabela não pisca entre duas verdades a cada clique.
		placeholderData: page ? (previous) => previous : undefined,
	});
}

/**
 * O QUE APAGAR — a seleção (com o recorte que ela veio) ou o recorte inteiro.
 *
 * As duas formas do servidor (`GapDeletion`, back). A seleção leva o `status`
 * de propósito: a lista mostra UMA linha por grupo de significado, e apagar só
 * o representante faria o grupo voltar com "22×" na leitura seguinte. Com o
 * recorte, vão os membros que a aba mostrava — e nunca uma pergunta aberta do
 * mesmo grupo, que é um pedido de ajuda com uma conversa à espera.
 */
export type GapDeletion =
	| { readonly ids: readonly string[]; readonly status: GapStatus }
	| { readonly filter: { readonly status: GapStatus } };

/**
 * Apagar em massa — `@MinRole('admin')` no servidor, e de verdade (DELETE, não
 * "descartada"). Uma linha curada aprovada a partir de uma lacuna SOBREVIVE:
 * apagar a pergunta não desensina a resposta aprovada, e a tela diz isso.
 */
export function useDeleteGaps() {
	const queries = useQueryClient();
	return useMutation({
		mutationFn: (body: GapDeletion) =>
			request<{ removed: number }>("/v3/memory/gaps/delete", { method: "POST", body }),
		onSuccess: () => {
			queries.invalidateQueries({ queryKey: ["memory", "gaps"] });
			// A origem de uma linha curada aponta para a lacuna; sem ela, a coluna
			// "perguntado como" continua (o texto foi copiado), mas o vínculo não.
			queries.invalidateQueries({ queryKey: ["memory", "curated"] });
		},
	});
}

/**
 * "Isto virou regra da empresa" — dito por uma pessoa, nunca inferido.
 *
 * It does NOT write the brief. An answer can be right for one case and wrong as
 * a rule ("dei 20% pro João porque ele é cliente antigo"), so the person edits
 * the brief in their own words and then closes the gap here. Writing it
 * automatically would put the exception into the rule.
 */
export function useMarkPromoted() {
	const queries = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			request<{ promoted: boolean }>(`/v3/memory/gaps/${id}/promoted`, { method: "POST" }),
		onSuccess: () => {
			queries.invalidateQueries({ queryKey: ["memory", "gaps"] });
			// A linha aprovada aparece na tabela do que a equipe ensinou.
			queries.invalidateQueries({ queryKey: ["memory", "curated"] });
		},
	});
}

/**
 * APROVAR o rascunho e roteá-lo — para o que a equipe ensinou (uma linha), ou para a library.
 *
 * O que entra é o `text` que a pessoa aprovou (o rascunho editado ou reescrito), no
 * `destination` que ela escolheu. A IA só propôs. É a única porta pela qual conhecimento
 * aprovado entra, e é `@MinRole('admin')` no servidor: um fato errado da empresa custa
 * todas as conversas e é invisível, então exige o clique de uma pessoa.
 */
export function useApproveKnowledge() {
	const queries = useQueryClient();
	return useMutation({
		mutationFn: (input: { id: string; destination: KnowledgeDestination; text: string }) =>
			request<{ approved: boolean; destination: KnowledgeDestination }>(
				`/v3/memory/gaps/${input.id}/approve`,
				{ method: "POST", body: { destination: input.destination, text: input.text } },
			),
		onSuccess: () => {
			queries.invalidateQueries({ queryKey: ["memory", "gaps"] });
			// A linha aprovada aparece na tabela do que a equipe ensinou.
			queries.invalidateQueries({ queryKey: ["memory", "curated"] });
		},
	});
}
